import type { OrgReportListItemFragment } from "@/graphql/generated";
import { REPORT_STATUS_ORDER } from "../constants";

export const REPORT_SORT_KEYS = ["period", "generated", "status"] as const;
export type ReportSortKey = (typeof REPORT_SORT_KEYS)[number];

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export interface SortState {
  key: ReportSortKey;
  direction: SortDirection;
}

export const DEFAULT_SORT: SortState = { key: "generated", direction: "desc" };

export function isReportSortKey(value: string): value is ReportSortKey {
  return (REPORT_SORT_KEYS as readonly string[]).includes(value);
}

export function isSortDirection(value: string): value is SortDirection {
  return (SORT_DIRECTIONS as readonly string[]).includes(value);
}

/**
 * Google-Drive-style toggle: clicking a different column starts at DESC
 * (newest / highest first); clicking the active column flips direction.
 */
export function nextSortState(current: SortState, clicked: ReportSortKey): SortState {
  if (current.key !== clicked) return { key: clicked, direction: "desc" };
  return { key: clicked, direction: current.direction === "desc" ? "asc" : "desc" };
}

export function sortReports(
  items: readonly OrgReportListItemFragment[],
  state: SortState,
): OrgReportListItemFragment[] {
  const sign = state.direction === "asc" ? 1 : -1;
  return [...items].sort((a, b) => sign * compare(a, b, state.key));
}

function compare(
  a: OrgReportListItemFragment,
  b: OrgReportListItemFragment,
  key: ReportSortKey,
): number {
  switch (key) {
    case "period":
      // periodStart is an ISO date string — lexicographic === chronological.
      return a.periodStart.localeCompare(b.periodStart);
    case "generated": {
      const av = a.generatedAt ?? a.createdAt;
      const bv = b.generatedAt ?? b.createdAt;
      return av.localeCompare(bv);
    }
    case "status":
      return REPORT_STATUS_ORDER[a.status] - REPORT_STATUS_ORDER[b.status];
  }
}
